'use client'
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {Form, notification, Spin} from "antd";
import Input from "@/app/components/form-fields/input/Input";
import Select from "@/app/components/form-fields/select/Select";
import {countryService} from "@/app/services/CountryService";
import classes from "./CoverSection.module.scss";
import {useRouter} from "next/navigation";
import {debounce} from "lodash";
import {getCookie} from "cookies-next";
import Carousel from "@/app/components/carousel/Carousel";
import MediatorCard from "@/app/components/mediator-card/MediatorCard";
import Image from 'next/image';
import NoResultsImg from "@/app/assets/images/mediators/noResults.svg";
import {LoadingOutlined} from "@ant-design/icons";
import Link from "next/link";


const CoverSection = () => {
    const {t} = useTranslation();
    const [error, setError] = useState(null);
    const [countries, setCountries] = useState([]);
    const [query, setQuery] = useState("");
    const [loading,setLoading] = useState(true);
    const [form] = Form.useForm();
    const [search, setSearch] = useState({
        name: '',
        company: '',
        country_id: '',
    });
    const [mediators,setMediators] = useState([]);
    const [headerText,setHeaderText] = useState(t('mediatorsSliderHeader'))

    const router = useRouter();


    const [api, contextHolder] = notification.useNotification();

    const openNotification = (type, placement, text) => {
        api[type]({
            message: `${type === 'error' ? t('notifications.error') : t('notifications.success')}`,
            description: text,
            placement,
        });
    };

    const fetchMediators = () => {
        setLoading(true);
        fetch(`${process.env.NEXT_PUBLIC_API_URL}/search?limit=10&name=${search.name}&company=${search.company}&country_id=${search.country_id ? search.country_id : ''}`, {
            headers: {
                'Content-type': 'application/json', 'Authorization': `Bearer ${getCookie('jwt')}`
            },
        })
            .then((res) => res.json())
            .then((data) => {
                setMediators(data.data);
                setLoading(false);
            }).catch((err) => {
            setLoading(false);
            openNotification('error', 'error', t('notifications.serverError'));
        })
    }

    const fetchTopMediators = () => {
        setLoading(true);
        fetch(`${process.env.NEXT_PUBLIC_API_URL}/top-mediators`)
            .then((res) => res.json())
            .then((data) => {
                setMediators(data.data);
                setLoading(false);
            }).catch((err) => {
                    openNotification('error', 'bottomRight', t('notifications.serverError'));
                    setLoading(false);
        })
    }


    useEffect(() => {
        countryService.getAll(query).then(setCountries).catch(setError);
    }, [query]);


    useEffect(() => {
        const errors = form.getFieldsError();
        let hasErrors = false; // Initialize hasErrors to false
        for (const fieldName in errors) {
            if (errors[fieldName].errors && errors[fieldName].errors.length > 0) {
                hasErrors = true; // Set hasErrors to true if any errors are found
                break; // Exit the loop once an error is found
            }
        }
        const isSearchEmpty = Object.values(search).every(value => value === '');
        if(isSearchEmpty) {
            setHeaderText(t('mediatorsSliderHeader'))
            fetchTopMediators();
        }else if(!hasErrors) {
            setHeaderText(t('searchPage.searchResults'))
            fetchMediators();
        }else {
            setHeaderText(t('mediatorsSliderHeader'))
            fetchTopMediators()
        }
    }, [search]);



    const countryOptions = countries.map(country => ({
        value: country.id,
        label: country.name,
    }));

    const onFinish = (values) => {
        let queryParams = {
            ...(values.name && {name: values.name}),
            ...(values.company && {company: values.company}),
            ...(values.country_id && values.country_id.value && values.country_id.value !== "null" && {
                country_id: values.country_id.value,
                country_label: values.country_id.label
            }),
        };
        const queryString = new URLSearchParams(queryParams).toString();
        router.push(`/search?${queryString}`);
    };

    const handleChange = debounce((e) => {
        if(e) {
            setSearch(prev => ({...prev, country_id: e.value}));
        }else{
            setSearch((prev) => {
                const newState = { ...prev };
                if (newState.hasOwnProperty('country_id')) {
                    delete newState.country_id;
                }
                return newState;
            });
            setQuery('')
        }
    }, 500);

    const handleSearch = debounce((e) => {
        setQuery(e);
    }, 500);

    const handleTextSearch = debounce((name,event) => {
        setSearch(prev => ({...prev, [name]: event.target.value}));
    }, 500);

    return (
        <>
            {contextHolder}
        <div className={classes.container}>
            <div className={classes.bgImage}></div>
            <div className={classes.descriptionContainer}>
                <h1 className={classes.title}>{t('coverSection.title')}</h1>
                <span className={classes.description}>{t('coverSection.marketplaceDescription')}</span>
            </div>
            <div className={classes.searchContainer}>
                <p>{t('coverSection.intro')} <span className={classes.lineBreak}/> {t('coverSection.lookingFor')}</p>
                <Form
                    form={form}
                    onFinish={onFinish}
                    initialValues={{
                        name: '',
                        company: '',
                        country_id: null,
                    }}
                >
                    <div className={classes.rowSearch}>
                        <div className={classes.inputGroup}>
                                <Form.Item
                                    name="name"
                                    rules={[
                                        {
                                            min: 2,
                                            max: 100,
                                            message: t("validation.nameLength"),
                                        },
                                    ]}
                                >
                                    <Input
                                        onChange={(event) => handleTextSearch('name',event)}
                                        className={classes.borderRadius}
                                        placeholder={t('placeholders.nameAndSurname')}
                                    />
                                </Form.Item>
                            <Form.Item
                                name="company"
                                rules={[
                                    {
                                        min: 2,
                                        max: 100,
                                        message: t("validation.nameLength"),
                                    },
                                ]}>
                                <Input
                                    onChange={(event) => handleTextSearch('company',event)}
                                    className={classes.border}
                                    placeholder={t('placeholders.company')}
                                />
                            </Form.Item>

                            <Form.Item
                                name="country_id"
                            >
                                <Select
                                    className={classes.customSelect}
                                    placeholder={t('placeholders.country')}
                                    options={countryOptions}
                                    filterOption={() => true}
                                    onChange={handleChange}
                                    onSearch={handleSearch}
                                    allowClear
                                    labelInValue
                                />
                            </Form.Item>

                            {/*<Button*/}
                            {/*    htmlType="submit"*/}
                            {/*    icon={<SearchOutlined />*/}
                            {/*}*/}
                            {/*    className={classes.searchIcon}*/}
                            {/*>*/}
                            {/*</Button>*/}
                        </div>
                        <button className={classes.advancedSearch} type={'submit'}>
                             {t('searchPage.advancedSearch')}
                        </button>
                    </div>
                    {/* {expand && (
                    <div className={`${classes.inputGroup} ${classes.line}`}>
                        <Input placeholder="Industry"/>
                        <Input className={classes.borders} placeholder="Position"/>
                        <Input placeholder="Linkedin profile"/>
                    </div>
                )}*/}
                </Form>
            </div>
        </div>
            <h3 className={`carouselHeader`}>{headerText}</h3>
            {mediators.length > 0 ? <div className={classes.mediatorSliderWrapper}><Carousel className={'slider'} sliderOptions={{pagination: false, gap: 20, fixedWidth: 495}}
                      data={mediators} CardComponent={MediatorCard} customCardClass={classes.mediatorCard}
            />
                {mediators.length >= 10 &&  <Link href={`search?name=${search.name}&company=${search.company}&country_id=${search.country_id ? search.country_id : ''}`}>
                    {t('seeAll')}
                </Link>}
            </div> : <div className={classes.noResults}>
                {!loading ?
                <>
                <Image src={NoResultsImg} alt={'no results image'}/>
                <h3>{t('searchPage.noResults')}</h3>
                <p>{t('searchPage.unsuccessfulSearch')}</p>
                </> :  <Spin indicator={<LoadingOutlined style={{ fontSize: 55 }} spin />} spinning={true} />
                }
            </div>}
        </>
    );
}
export default CoverSection;